/**
 * Ontario holidays, computed, for the on-call rotation.
 *
 * This module is the source of truth (CLAUDE.md §5 and §11). It works
 * offline, reads no clock and calls no API. Only the holidays that matter to
 * the rotation are here: the ones that make a long weekend, and the three
 * days that define the Christmas and New Year weeks. Easter Monday, Truth and
 * Reconciliation Day and Remembrance Day are ordinary work days for the group
 * and are deliberately absent (owner, 26 September 2026).
 *
 * The rules that decide *which week* a holiday affects live in
 * `../weeks/weeks.ts`; this file only says when each holiday is and which
 * rule applies to it.
 */
import {
  FRIDAY,
  MONDAY,
  SATURDAY,
  SUNDAY,
  addDays,
  dayOfWeek,
  isoDate,
  nthWeekdayOfMonth,
  weekdayOnOrBefore,
  type ISODate,
} from '../dates/iso.ts';

export type HolidayId =
  | 'new-year'
  | 'family-day'
  | 'good-friday'
  | 'victoria-day'
  | 'canada-day'
  | 'civic-holiday'
  | 'labour-day'
  | 'thanksgiving'
  | 'christmas'
  | 'boxing-day';

/**
 * How a holiday makes a long weekend, if it does.
 *
 * - `preceding-week`: a Monday holiday. The week *before* it is the
 *   long-weekend week; its physician covers through the holiday Monday.
 * - `containing-week`: a Friday holiday. The week that contains it is the
 *   long-weekend week.
 *
 * These are deliberately different (CLAUDE.md §6). A Monday holiday belongs
 * to the weekend before it; a Friday holiday belongs to the weekend after it.
 */
export type LongWeekendRule = 'preceding-week' | 'containing-week';

export type SpecialDay = 'christmas' | 'boxing-day' | 'new-year';

export interface Holiday {
  id: HolidayId;
  name: string;
  /** The calendar day of the holiday. */
  date: ISODate;
  /**
   * The statutory day off. Differs from `date` only for Canada Day on a
   * weekend, when the following Monday is the holiday in lieu.
   */
  observed: ISODate;
  /** How this holiday makes a long weekend, or null when it does not. */
  longWeekend: LongWeekendRule | null;
  /** Set for the three days that define the Christmas and New Year weeks. */
  special: SpecialDay | null;
}

export interface HolidayOptions {
  /**
   * The Civic Holiday (first Monday of August) is not a statutory holiday in
   * Ontario, but the group observes it by default. It is the only optional
   * holiday.
   */
  civicHoliday: boolean;
}

export const DEFAULT_HOLIDAY_OPTIONS: HolidayOptions = { civicHoliday: true };

/** Easter Sunday by the anonymous Gregorian algorithm (Meeus/Jones/Butcher). */
export function easterSunday(year: number): ISODate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoDate(year, month, day);
}

export function familyDay(year: number): ISODate {
  return nthWeekdayOfMonth(year, 2, MONDAY, 3);
}

export function goodFriday(year: number): ISODate {
  return addDays(easterSunday(year), -2);
}

/** The Monday before 25 May: the last Monday on or before 24 May. */
export function victoriaDay(year: number): ISODate {
  return weekdayOnOrBefore(isoDate(year, 5, 24), MONDAY);
}

export function canadaDay(year: number): ISODate {
  return isoDate(year, 7, 1);
}

export function civicHoliday(year: number): ISODate {
  return nthWeekdayOfMonth(year, 8, MONDAY, 1);
}

export function labourDay(year: number): ISODate {
  return nthWeekdayOfMonth(year, 9, MONDAY, 1);
}

export function thanksgiving(year: number): ISODate {
  return nthWeekdayOfMonth(year, 10, MONDAY, 2);
}

/**
 * Canada Day is the one holiday whose weekday changes what it does.
 *
 * - On a Saturday or Sunday, the following Monday is the statutory holiday in
 *   lieu, and that Monday follows the Monday rule (owner, 26 September 2026).
 * - On a Monday, the Monday rule applies directly.
 * - On a Friday, the week containing it is the long-weekend week.
 * - On Tuesday to Thursday it is not a long weekend at all.
 */
function canadaDayHoliday(year: number): Holiday {
  const date = canadaDay(year);
  const dow = dayOfWeek(date);
  let observed = date;
  if (dow === SATURDAY) observed = addDays(date, 2);
  if (dow === SUNDAY) observed = addDays(date, 1);
  let longWeekend: LongWeekendRule | null = null;
  if (dayOfWeek(observed) === MONDAY) longWeekend = 'preceding-week';
  else if (dow === FRIDAY) longWeekend = 'containing-week';
  return { id: 'canada-day', name: 'Canada Day', date, observed, longWeekend, special: null };
}

function monday(id: HolidayId, name: string, date: ISODate): Holiday {
  return { id, name, date, observed: date, longWeekend: 'preceding-week', special: null };
}

function special(id: HolidayId, name: string, date: ISODate, kind: SpecialDay): Holiday {
  return { id, name, date, observed: date, longWeekend: null, special: kind };
}

/** Every holiday the rotation cares about in a calendar year, in date order. */
export function ontarioHolidays(year: number, options: HolidayOptions = DEFAULT_HOLIDAY_OPTIONS): Holiday[] {
  const list: Holiday[] = [
    special('new-year', "New Year's Day", isoDate(year, 1, 1), 'new-year'),
    monday('family-day', 'Family Day', familyDay(year)),
    {
      id: 'good-friday',
      name: 'Good Friday',
      date: goodFriday(year),
      observed: goodFriday(year),
      longWeekend: 'containing-week',
      special: null,
    },
    monday('victoria-day', 'Victoria Day', victoriaDay(year)),
    canadaDayHoliday(year),
    monday('labour-day', 'Labour Day', labourDay(year)),
    monday('thanksgiving', 'Thanksgiving', thanksgiving(year)),
    special('christmas', 'Christmas Day', isoDate(year, 12, 25), 'christmas'),
    special('boxing-day', 'Boxing Day', isoDate(year, 12, 26), 'boxing-day'),
  ];
  if (options.civicHoliday) list.push(monday('civic-holiday', 'Civic Holiday', civicHoliday(year)));
  return list.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
